(function () {
  class ProductGallery {
    constructor(container) {
      this.container = container;
      this.enableAutoplay = container.dataset.videoAutoplay === "true";
      this.isInViewport = false;
      this.mainImages = Array.from(container.querySelectorAll(".shop-main-image"));
      this.zoomWrappers = Array.from(container.querySelectorAll(".shop-image-zoom-wrapper"));
      this.thumbnails = Array.from(container.querySelectorAll(".shop-thumbnail"));
      this.dots = Array.from(container.querySelectorAll(".shop-dot"));
      this.desktopPrevArrow = container.querySelector(".shop-desktop-prev-arrow");
      this.desktopNextArrow = container.querySelector(".shop-desktop-next-arrow");
      this.mobilePrevArrows = Array.from(container.querySelectorAll(".shop-mobile-prev-arrow"));
      this.mobileNextArrows = Array.from(container.querySelectorAll(".shop-mobile-next-arrow"));
      this.indicatorsContainer = container.querySelector(".shop-indicators, #thumbnail-indicators");
      const initialActiveIndex = this.mainImages.findIndex((media) => media.classList.contains("active"));
      this.currentIndex = initialActiveIndex >= 0 ? initialActiveIndex : 0;
      this.maxIndex = this.mainImages.length - 1;
      this.virtualMediaIds = new Set(
        this.mainImages
          .filter((media) => media.dataset.virtualMedia === "true")
          .map((media) => String(media.dataset.mediaId || ""))
          .filter(Boolean),
      );
      this.lastNativeMediaId = String(this.mainImages[this.currentIndex]?.dataset.mediaId || "");
      this.programmaticScrollTarget = null;
      this.programmaticScrollTimer = 0;
      this.activeMediaIndex = null;
      this.init();
    }
    init() {
      this.ensureImageLoaded(this.currentIndex);
      this.mainImages.forEach((img, i) => {
        img.classList.toggle("active", i === this.currentIndex);
      });
      this.zoomWrappers.forEach((wrapper, i) => {
        const mediaId = wrapper.dataset.mediaId;
        const activeMediaId = this.mainImages[this.currentIndex]?.dataset.mediaId;
        wrapper.classList.toggle("active", mediaId === activeMediaId);
      });
      this.thumbnails.forEach((thumb, i) => {
        const isActive = i === this.currentIndex;
        thumb.classList.toggle("active", isActive);
        thumb.setAttribute("aria-current", isActive ? "true" : "false");
      });
      this.dots.forEach((dot, i) => {
        const isActive = i === this.currentIndex;
        dot.classList.toggle("active", isActive);
        dot.setAttribute("aria-current", isActive ? "true" : "false");
      });
      this.syncNavigationControls();
      this.attachEvents();
      this.setupThumbnailStripDrag();
      this.setupMobileScroller();
      this.syncThumbColumnHeight();
      this.setupViewportObserver();
      this.setupPullDemo();
      this.handleVideoAutoplay(this.currentIndex);
    }
    setupPullDemo() {
      const video = this.container.querySelector("[data-fourpaw-pull-demo]");
      const button = this.container.querySelector("[data-fourpaw-pull-demo-play]");
      if (!video || !button || button.dataset.initialized) return;
      button.dataset.initialized = "true";
      video.controls = false;
      button.hidden = false;
      button.addEventListener("click", () => {
        // Native controls take over after the first intentional play.
        video.controls = true;
        button.hidden = true;
        video.focus();
        video.play().catch(() => {
          // Keep native controls available for a network or playback retry.
          video.controls = true;
        });
      });
    }
    setupViewportObserver() {
      if (!("IntersectionObserver" in window)) {
        this.isInViewport = true;
        return;
      }
      this.viewportObserver = new IntersectionObserver(
        (entries) => {
          const nextState = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.25);
          if (nextState === this.isInViewport) return;
          this.isInViewport = nextState;
          this.handleVideoAutoplay(this.currentIndex);
        },
        { threshold: [0, 0.25, 0.5] },
      );
      this.viewportObserver.observe(this.container);
    }
    slideForIndex(index) {
      const media = this.mainImages[index];
      if (!media) return null;
      return media.closest(".shop-image-zoom-wrapper") || media;
    }
    setupMobileScroller() {
      this.mobileScroller = this.container.querySelector(".shop-image-container");
      if (!this.mobileScroller) return;

      let frame = 0;
      const syncIndexFromScroll = () => {
        frame = 0;
        if (!window.matchMedia("(max-width: 799px)").matches) return;
        const scrollerRect = this.mobileScroller.getBoundingClientRect();
        const scrollerCenter = scrollerRect.left + scrollerRect.width / 2;
        let nearestIndex = this.currentIndex;
        let nearestDistance = Number.POSITIVE_INFINITY;
        this.mainImages.forEach((_, index) => {
          if (!this.isIndexNavigable(index)) return;
          const slide = this.slideForIndex(index);
          if (!slide || slide.hidden) return;
          const rect = slide.getBoundingClientRect();
          const distance = Math.abs(rect.left + rect.width / 2 - scrollerCenter);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = index;
          }
        });
        if (this.programmaticScrollTarget !== null) {
          if (nearestIndex === this.programmaticScrollTarget) {
            this.programmaticScrollTarget = null;
            window.clearTimeout(this.programmaticScrollTimer);
            this.programmaticScrollTimer = 0;
          }
          return;
        }
        if (nearestIndex !== this.currentIndex) {
          this.changeImage(nearestIndex, { fromScroll: true, source: "swipe" });
        }
      };
      this.mobileScroller.addEventListener(
        "scroll",
        () => {
          if (frame) return;
          frame = window.requestAnimationFrame(syncIndexFromScroll);
        },
        { passive: true },
      );
      window.requestAnimationFrame(() => this.scrollMobileTo(this.currentIndex, "auto"));
    }
    scrollMobileTo(index, behavior = "smooth") {
      if (!this.mobileScroller || !window.matchMedia("(max-width: 799px)").matches) return;
      const slide = this.slideForIndex(index);
      if (!slide || slide.hidden) return;
      this.programmaticScrollTarget = index;
      window.clearTimeout(this.programmaticScrollTimer);
      this.programmaticScrollTimer = window.setTimeout(() => {
        this.programmaticScrollTarget = null;
        this.programmaticScrollTimer = 0;
      }, 1200);
      this.mobileScroller.scrollTo({ left: slide.offsetLeft, behavior });
    }
    ensureImageLoaded(index) {
      const image = this.mainImages[index];
      if (!image || image.tagName !== "IMG") return;
      if (image.getAttribute("src") || image.getAttribute("srcset")) return;

      const srcset = image.dataset.srcset;
      const src = image.dataset.src;
      if (srcset) image.setAttribute("srcset", srcset);
      if (src) image.setAttribute("src", src);
      image.dataset.loaded = "true";
    }
    attachEvents() {
      this.thumbnails.forEach((thumb, i) => {
        thumb.addEventListener("click", (e) => {
          if (this._thumbStripDragEnded) {
            this._thumbStripDragEnded = false;
            e.preventDefault();
            return;
          }
          this.changeImage(i, { source: "thumbnail" });
        });
      });
      this.dots.forEach((dot, i) => {
        dot.addEventListener("click", () => this.changeImage(i, { source: "dot" }));
      });
      if (this.desktopPrevArrow) {
        this.desktopPrevArrow.addEventListener("click", () => {
          this.changeImage(this.getAdjacentIndex(-1), { source: "arrow" });
        });
      }
      if (this.desktopNextArrow) {
        this.desktopNextArrow.addEventListener("click", () => {
          this.changeImage(this.getAdjacentIndex(1), { source: "arrow" });
        });
      }
      this.mobilePrevArrows.forEach((arrow) => {
        if (arrow) {
          arrow.addEventListener("click", () => {
            this.changeImage(this.getAdjacentIndex(-1), { source: "arrow" });
          });
        }
      });
      this.mobileNextArrows.forEach((arrow) => {
        if (arrow) {
          arrow.addEventListener("click", () => {
            this.changeImage(this.getAdjacentIndex(1), { source: "arrow" });
          });
        }
      });
      if (this.indicatorsContainer) {
        this.indicatorsContainer.addEventListener("click", (e) => {
          if (e.target.classList.contains("shop-indicator-dot")) {
            const idx = parseInt(e.target.getAttribute("data-index"));
            if (!isNaN(idx)) this.changeImage(idx, { source: "dot" });
          }
        });
      }
    }
    isIndexNavigable(index) {
      const thumbnail = this.thumbnails[index];
      return !thumbnail || !thumbnail.classList.contains("visually-hidden");
    }
    getNavigableIndexes() {
      return this.mainImages
        .map((_, index) => index)
        .filter((index) => this.isIndexNavigable(index));
    }
    getAdjacentIndex(direction) {
      const navigableIndexes = this.getNavigableIndexes();
      if (!navigableIndexes.length) return this.currentIndex;
      const currentPosition = navigableIndexes.indexOf(this.currentIndex);
      if (currentPosition < 0) {
        return direction < 0
          ? navigableIndexes[navigableIndexes.length - 1]
          : navigableIndexes[0];
      }
      const nextPosition =
        (currentPosition + direction + navigableIndexes.length) % navigableIndexes.length;
      return navigableIndexes[nextPosition];
    }
    syncNavigationControls() {
      this.thumbnails.forEach((thumbnail, index) => {
        const isHiddenVariant = !this.isIndexNavigable(index);
        thumbnail.hidden = isHiddenVariant;
        thumbnail.setAttribute("aria-hidden", isHiddenVariant ? "true" : "false");
        const slide = this.slideForIndex(index);
        if (slide) {
          slide.hidden = isHiddenVariant;
          slide.setAttribute("aria-hidden", isHiddenVariant ? "true" : "false");
        }
      });
      this.dots.forEach((dot, index) => {
        const isHiddenVariant = !this.isIndexNavigable(index);
        dot.hidden = isHiddenVariant;
        dot.setAttribute("aria-hidden", isHiddenVariant ? "true" : "false");
      });
      this.createIndicators();
    }
    setupThumbnailStripDrag() {
      if (!window.matchMedia("(min-width: 800px)").matches) return;
      const self = this;
      self._activeThumbDrag = null;

      const duringDrag = (e) => {
        const a = self._activeThumbDrag;
        if (!a || a.pointerId !== e.pointerId) return;
        e.preventDefault();
        if (a.isVertical) {
          const dy = a.startY - e.clientY;
          if (!a.dragStarted && Math.abs(dy) > 5) {
            a.dragStarted = true;
          }
          if (a.dragStarted) {
            a.scrollEl.setAttribute("data-dragging", "1");
            const maxScroll = a.scrollEl.scrollHeight - a.scrollEl.clientHeight;
            const newTop = Math.max(0, Math.min(maxScroll, a.startScroll + dy));
            a.scrollEl.scrollTop = newTop;
            if (!a._loggedScroll) {
              a._loggedScroll = true;
            }
          }
        } else {
          const dx = a.startX - e.clientX;
          if (!a.dragStarted && Math.abs(dx) > 5) {
            a.dragStarted = true;
          }
          if (a.dragStarted) {
            a.scrollEl.setAttribute("data-dragging", "1");
            a.scrollEl.scrollLeft = a.startScroll + dx;
          }
        }
      };

      const stopDragging = (e) => {
        const a = self._activeThumbDrag;
        if (!a || (e && a.pointerId !== e.pointerId)) return;
        if (a.dragStarted) {
          self._thumbStripDragEnded = true;
        } else if (a.downTarget) {
          const i = self.thumbnails.indexOf(a.downTarget);
          if (i >= 0) self.changeImage(i, { source: "thumbnail" });
        }
        a.scrollEl.removeAttribute("data-dragging");
        self._activeThumbDrag = null;
      };

      document.addEventListener("pointermove", duringDrag, { passive: false });
      document.addEventListener("pointerup", stopDragging, { passive: true });
      document.addEventListener("pointercancel", stopDragging, { passive: true });

      const strips = Array.from(this.container.querySelectorAll(".shop-thumbnails"));
      strips.forEach((thumbStrip) => {
        const wrapper = thumbStrip.closest(".shop-thumbnails-wrapper");
        const isVertical = wrapper && wrapper.classList.contains("left-column-layout");
        const scrollEl = thumbStrip;

        const startDragging = (e) => {
          if (e.button !== 0 && e.type === "mousedown") return;
          const startScroll = isVertical ? scrollEl.scrollTop : scrollEl.scrollLeft;
          self._activeThumbDrag = {
            scrollEl,
            pointerId: e.pointerId,
            startX: e.clientX,
            startY: e.clientY,
            startScroll,
            isVertical,
            downTarget: e.target.closest(".shop-thumbnail"),
            dragStarted: false
          };
          scrollEl.setAttribute("data-dragging", "0");
          e.preventDefault();
        };

        if (!scrollEl) return;
        const preventNativeDrag = (e) => e.preventDefault();
        scrollEl.addEventListener("dragstart", preventNativeDrag, { capture: true });
        scrollEl.addEventListener("pointerdown", startDragging, { passive: false });
      });
    }
    syncThumbColumnHeight() {
      if (!window.matchMedia("(min-width: 768px)").matches) return;
      const imageContainer = this.container.querySelector(".shop-image-container");
      const root = this.container.closest(".shop-carousel-container") || this.container;
      if (!imageContainer || !root) return;
      const setHeight = () => {
        const cw = imageContainer.offsetWidth;
        const ch = imageContainer.offsetHeight;
        const activeImg = imageContainer.querySelector(".shop-image-zoom-wrapper.active .shop-main-image, .shop-main-image.active");
        const img = activeImg && activeImg.tagName === "IMG" ? activeImg : imageContainer.querySelector("img.shop-main-image.active");
        let h = ch;
        if (img && img.naturalWidth > 0 && img.naturalHeight > 0) {
          const scale = Math.min(cw / img.naturalWidth, ch / img.naturalHeight);
          h = Math.round(img.naturalHeight * scale);
        }
        if (h > 0) root.style.setProperty("--thumb-column-height", h + "px");
      };
      setHeight();
      this._setThumbColumnHeight = setHeight;
      const ro = new ResizeObserver(() => setHeight());
      ro.observe(imageContainer);
      imageContainer.querySelectorAll("img.shop-main-image").forEach((img) => {
        if (img.complete) setHeight();
        else img.addEventListener("load", setHeight);
      });
    }
    createIndicators() {
      if (!this.indicatorsContainer) return;
      this.indicatorsContainer.innerHTML = "";
      this.mainImages.forEach((_, i) => {
        if (!this.isIndexNavigable(i)) return;
        const isActive = i === this.currentIndex;
        const sourceControl = this.thumbnails[i] || this.dots[i];
        if (!sourceControl) return;
        const dot = document.createElement("button");
        dot.type = "button";
        dot.className = "shop-indicator-dot" + (isActive ? " active" : "");
        dot.setAttribute("data-index", i);
        dot.setAttribute("aria-label", sourceControl.getAttribute("aria-label") || `Gallery media ${i + 1}`);
        const ariaControls = sourceControl.getAttribute("aria-controls");
        if (ariaControls) dot.setAttribute("aria-controls", ariaControls);
        dot.setAttribute("aria-current", isActive ? "true" : "false");
        this.indicatorsContainer.appendChild(dot);
      });
    }
    registerVirtualMedia({ id, media, thumbnail }) {
      const mediaId = String(id || "");
      if (!mediaId || !media || !thumbnail) return false;

      const existingIndex = this.mainImages.findIndex(
        (item) => String(item.dataset.mediaId || "") === mediaId,
      );
      if (existingIndex >= 0) return existingIndex;

      const imageContainer = this.container.querySelector(".shop-image-container");
      const thumbnailContainer = this.container.querySelector(".shop-thumbnails");
      if (!imageContainer || !thumbnailContainer) return false;

      const mainMedia = media.matches(".shop-main-image")
        ? media
        : media.querySelector(".shop-main-image");
      if (!mainMedia) return false;

      media.dataset.mediaId = mediaId;
      media.dataset.virtualMedia = "true";
      mainMedia.dataset.mediaId = mediaId;
      mainMedia.dataset.virtualMedia = "true";
      thumbnail.dataset.mediaId = mediaId;
      thumbnail.dataset.virtualMedia = "true";

      const navigation = imageContainer.querySelector(
        ":scope > .shop-mobile-arrows, :scope > .shop-desktop-arrows",
      );
      imageContainer.insertBefore(media, navigation || null);
      thumbnailContainer.append(thumbnail);

      this.virtualMediaIds.add(mediaId);
      this.mainImages = Array.from(this.container.querySelectorAll(".shop-main-image"));
      this.zoomWrappers = Array.from(this.container.querySelectorAll(".shop-image-zoom-wrapper"));
      this.thumbnails = Array.from(this.container.querySelectorAll(".shop-thumbnail"));
      this.maxIndex = this.mainImages.length - 1;

      thumbnail.addEventListener("click", (event) => {
        if (this._thumbStripDragEnded) {
          this._thumbStripDragEnded = false;
          event.preventDefault();
          return;
        }
        this.setMediaById(mediaId, { source: "thumbnail" });
      });

      this.syncNavigationControls();
      this.syncThumbColumnHeight();
      return this.mainImages.findIndex((item) => String(item.dataset.mediaId || "") === mediaId);
    }
    setMediaById(id, { source } = {}) {
      const mediaId = String(id || "");
      const index = this.mainImages.findIndex(
        (item) => String(item.dataset.mediaId || "") === mediaId,
      );
      if (index < 0 || !this.isIndexNavigable(index)) return false;
      this.changeImage(index, { source });
      return true;
    }
    setVirtualMediaHidden(id, hidden) {
      const mediaId = String(id || "");
      if (!this.virtualMediaIds.has(mediaId)) return;
      const index = this.mainImages.findIndex(
        (item) => String(item.dataset.mediaId || "") === mediaId,
      );
      const thumbnail = this.thumbnails[index];
      const media = this.mainImages[index];
      thumbnail?.classList.toggle("visually-hidden", Boolean(hidden));
      if (media) {
        media.hidden = Boolean(hidden);
        media.setAttribute("aria-hidden", hidden ? "true" : "false");
      }
      if (hidden && index === this.currentIndex) this.restoreNative();
      this.syncNavigationControls();
    }
    restoreNative() {
      let index = this.mainImages.findIndex(
        (item) => String(item.dataset.mediaId || "") === this.lastNativeMediaId,
      );
      if (index < 0 || !this.isIndexNavigable(index)) {
        index = this.mainImages.findIndex(
          (item, candidateIndex) =>
            !this.virtualMediaIds.has(String(item.dataset.mediaId || "")) &&
            this.isIndexNavigable(candidateIndex),
        );
      }
      if (index >= 0) this.changeImage(index);
    }
    changeImage(index, { fromScroll = false, source } = {}) {
      if (index < 0 || index > this.maxIndex) return;
      const previousIndex = this.currentIndex;
      this.ensureImageLoaded(index);
      this.mainImages.forEach((img, i) => {
        img.classList.toggle("active", i === index);
      });
      const activeMediaId = this.mainImages[index]?.dataset.mediaId;
      this.zoomWrappers.forEach((wrapper) => {
        wrapper.classList.toggle("active", wrapper.dataset.mediaId === activeMediaId);
      });
      this.thumbnails.forEach((thumb, i) => {
        const isActive = i === index;
        thumb.classList.toggle("active", isActive);
        thumb.setAttribute("aria-current", isActive ? "true" : "false");
      });
      this.dots.forEach((dot, i) => {
        const isActive = i === index;
        dot.classList.toggle("active", isActive);
        dot.setAttribute("aria-current", isActive ? "true" : "false");
      });
      if (this.indicatorsContainer) {
        Array.from(this.indicatorsContainer.children).forEach((dot) => {
          const isActive = Number(dot.getAttribute("data-index")) === index;
          dot.classList.toggle("active", isActive);
          dot.setAttribute("aria-current", isActive ? "true" : "false");
        });
      }
      this.currentIndex = index;
      if (!this.virtualMediaIds.has(String(activeMediaId || ""))) {
        this.lastNativeMediaId = String(activeMediaId || "");
      }
      this.scrollToThumbnail(index);
      if (!fromScroll) this.scrollMobileTo(index);
      this.handleVideoAutoplay(index);
      const nextIndex = this.getAdjacentIndex(1);
      if (nextIndex !== index) {
        window.setTimeout(() => this.ensureImageLoaded(nextIndex), 350);
      }
      if (this._setThumbColumnHeight) requestAnimationFrame(() => this._setThumbColumnHeight());
      if (source && previousIndex !== index) {
        const activeMedia = this.mainImages[index];
        const isPersonalizationPreview = activeMedia?.dataset.virtualMedia === "true";
        const mediaType = isPersonalizationPreview
          ? "personalization"
          : activeMedia?.dataset.videoType
            ? "video"
            : activeMedia?.querySelector?.("model-viewer")
              ? "model"
              : activeMedia
                ? "image"
                : "unknown";
        this.container.dispatchEvent(
          new CustomEvent("product-gallery:interaction", {
            bubbles: true,
            detail: {
              position: index + 1,
              media_count: this.getNavigableIndexes().length,
              media_type: mediaType,
              source,
              is_personalization_preview: isPersonalizationPreview,
            },
          }),
        );
      }
    }
    scrollToThumbnail(index) {
      if (!this.thumbnails[index]) return;
      const thumbnail = this.thumbnails[index];
      const container = thumbnail.parentElement;
      if (!container) return;
      const containerWidth = container.offsetWidth;
      const thumbnailWidth = thumbnail.offsetWidth;
      const thumbnailLeft = thumbnail.offsetLeft;
      const scrollPosition = thumbnailLeft - containerWidth / 2 + thumbnailWidth / 2;
      const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
      container.scrollTo({ left: scrollPosition, behavior });
    }
    pauseMedia(media) {
      if (!media) return;
      const video = media.querySelector("video");
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
      const iframe = media.querySelector("iframe");
      if (!iframe) return;
      const src = iframe.src || "";
      if (src.includes("youtube.com")) {
        iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', "*");
      } else if (src.includes("vimeo.com")) {
        iframe.contentWindow.postMessage('{"method":"pause"}', "*");
      }
    }
    handleVideoAutoplay(index) {
      const previousIndex = this.activeMediaIndex;
      const previousMedia = previousIndex === null ? null : this.mainImages[previousIndex];
      if (previousMedia && (previousIndex !== index || !this.isInViewport)) {
        this.pauseMedia(previousMedia);
      }

      const activeMedia = this.mainImages[index];
      if (!activeMedia) return;
      this.activeMediaIndex = index;
      if (!this.enableAutoplay || !this.isInViewport) return;

      const videoToPlay = activeMedia.querySelector("video");
      if (videoToPlay) videoToPlay.play().catch(() => {});

      const iframeToPlay = activeMedia.querySelector("iframe");
      if (iframeToPlay) {
        const src = iframeToPlay.src || "";
        if (src.includes("youtube.com")) {
          iframeToPlay.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', "*");
        } else if (src.includes("vimeo.com")) {
          iframeToPlay.contentWindow.postMessage('{"method":"play"}', "*");
        }
      }
    }
    updateWithVariant(variant) {
      this.syncNavigationControls();
      let matchIndex = -1;
      if (variant && (variant.mediaId || variant.variantId || variant.imageUrl)) {
        this.mainImages.forEach((img, i) => {
          if (
            (variant.mediaId && img.dataset.mediaId == variant.mediaId) ||
            (variant.variantId && img.dataset.variantId == variant.variantId) ||
            (variant.imageUrl &&
              (img.src.includes(variant.imageUrl) || (img.dataset.src && img.dataset.src.includes(variant.imageUrl))))
          ) {
            matchIndex = i;
          }
        });
      }
      if (matchIndex >= 0) {
        this.changeImage(matchIndex);
      } else {
        const firstNavigableIndex = this.getNavigableIndexes()[0] ?? 0;
        this.changeImage(firstNavigableIndex);
      }
    }
  }
  const galleryRegistry = [];
  function initAll() {
    document.querySelectorAll(".product-gallery").forEach((container) => {
      if (!container.__galleryInstance) {
        const gallery = new ProductGallery(container);
        container.__galleryInstance = gallery;
        galleryRegistry.push(gallery);
        container.dispatchEvent(
          new CustomEvent("product-gallery:ready", { bubbles: true, detail: { gallery } }),
        );
      }
    });
  }
  document.addEventListener("variantImageSelected", function (e) {
    if (e.detail && (e.detail.variantId || e.detail.mediaId || e.detail.imageUrl)) {
      galleryRegistry.forEach((gallery) => gallery.updateWithVariant(e.detail));
    }
  });
  document.addEventListener("variant:imageChanged", function (e) {
    if (e.detail && (e.detail.variantId || e.detail.mediaId || e.detail.imageUrl)) {
      galleryRegistry.forEach((gallery) => gallery.updateWithVariant(e.detail));
    }
  });
  window.ProductGallery = { initAll, getAll: () => galleryRegistry };
  document.addEventListener("DOMContentLoaded", initAll);
})();
